
/**
 * Simple and reliable cross-platform UUID v4 generator
 * This implementation completely avoids using crypto.randomUUID which causes compatibility issues
 */

// Generate a UUID v4 (random) - using Math.random() for maximum compatibility
export function generateUUID(): string {
  // Helper function to generate random hex digits
  const randomHex = (count: number): string => {
    let result = '';
    for (let i = 0; i < count; i++) {
      result += Math.floor(Math.random() * 16).toString(16);
    }
    return result;
  };
  
  // Format according to RFC4122 version 4
  // xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // where x is any random hex digit and y is one of 8, 9, a, or b
  const uuid = [
    randomHex(8),
    randomHex(4),
    // Version 4 UUID always has the 3rd group start with a 4
    '4' + randomHex(3),
    // RFC4122 variant: first digit must be 8, 9, a, or b
    (8 + Math.floor(Math.random() * 4)).toString(16) + randomHex(3),
    // Use timestamp in last group for added uniqueness
    Date.now().toString(16).padStart(12, '0').slice(-12)
  ].join('-');

  return uuid;
}

/**
 * Check if browser has secure random number generator
 * This is used for informational purposes only
 */
export function hasSecureRandomSupport(): boolean {
  try {
    return typeof window !== 'undefined' && 
           window.crypto !== undefined && 
           typeof window.crypto.getRandomValues === 'function';
  } catch (e) {
    return false;
  }
}

/**
 * Generate a shorter unique ID for cases when full UUID is too long
 * @param length The desired length of the ID (default: 10)
 * @returns A short unique ID
 */
export function generateShortId(length: number = 10): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  // Add timestamp prefix for added uniqueness (base36 encoded - 4 chars)
  const timestampPart = Date.now().toString(36).slice(-4);
  result += timestampPart;
  
  // Fill remaining length with random characters
  const randomLength = Math.max(0, length - timestampPart.length);
  
  for (let i = 0; i < randomLength; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    result += chars.charAt(randomIndex);
  }
  
  return result;
}
